import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, GraduationCap, Briefcase, Network, BarChart3, Globe2, Sparkles } from "lucide-react";
import { useLang } from "@/lib/i18n";

const ICONS = [Briefcase, Sparkles, BarChart3, Network, GraduationCap, Globe2];

export default function Careers() {
  const { t } = useLang();
  const highlights = t("careers.highlights") || [];

  return (
    <section
      id="karriere"
      data-testid="careers-section"
      className="relative py-14 lg:py-20 overflow-hidden bg-[#F3F4F6]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="relative rounded-3xl overflow-hidden bg-[#0A192F] shadow-2xl"
        >
          {/* Gradient glows */}
          <div className="absolute -top-40 -right-40 w-[480px] h-[480px] rounded-full bg-[#C9A96E]/15 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-32 -left-32 w-[480px] h-[480px] rounded-full bg-[#1E3A8A]/40 blur-3xl pointer-events-none" />
          {/* Tech grid */}
          <div
            className="absolute inset-0 opacity-[0.06] pointer-events-none"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
              backgroundSize: "80px 80px",
            }}
          />
          {/* Inner gold frame */}
          <div className="absolute inset-3 rounded-2xl border border-[#C9A96E]/15 pointer-events-none" />

          <div className="relative px-6 sm:px-12 lg:px-16 py-14 lg:py-20 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-extrabold text-[#C9A96E]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#C9A96E]" />
                {t("careers.eyebrow")}
              </span>
              <h2 className="mt-5 font-heading font-bold text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1]">
                {t("careers.title")} <span className="text-[#C9A96E]">{t("careers.titleAccent")}</span>
              </h2>
              <p className="mt-5 text-lg lg:text-xl text-white/90 font-light leading-relaxed max-w-2xl">
                {t("careers.lead")}
              </p>
              <p className="mt-5 text-base text-slate-300 leading-relaxed max-w-2xl">
                {t("careers.body")}
              </p>

              <div className="mt-8 flex flex-wrap gap-2 max-w-xl">
                {highlights.map((label, i) => {
                  const Icon = ICONS[i % ICONS.length];
                  return (
                    <span
                      key={label}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-200"
                    >
                      <Icon size={12} className="text-[#93C5FD]" />
                      {label}
                    </span>
                  );
                })}
              </div>

              <Link
                to="/karriere/trainee-consultant"
                data-testid="careers-cta"
                className="mt-10 group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-white text-[#0A192F] font-semibold hover:bg-[#C9A96E] hover:text-white transition-all duration-300"
              >
                {t("careers.cta")}
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </div>

            <div className="lg:col-span-5">
              <div className="relative aspect-[4/5] rounded-2xl overflow-hidden ring-1 ring-white/10 max-w-sm ml-auto">
                <img
                  src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1200&q=80"
                  alt="Young professionals working with senior consultants"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#0A192F]/70 via-transparent to-transparent" />
                <div className="absolute bottom-5 left-5 right-5 text-white">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9A96E] font-semibold">
                    {t("careers.role.eyebrow")}
                  </div>
                  <div className="font-heading text-lg font-semibold mt-1 leading-tight">
                    {t("careers.role.title")}
                  </div>
                  <div className="text-sm text-white/80 mt-1">
                    {t("careers.role.subtitle")}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
